import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { FormValidator } from 'src/app/validators/form-validator';

export type NumberRangeType = {
    startNumber: number | null,
    endNumber: number | null
};

type NumberRangeFormGroupType = {
    startNumber: FormControl<number | null>,
    endNumber: FormControl<number | null>
};

@Component({
    selector: 'app-number-range',
    templateUrl: './number-range.component.html',
    styleUrls: ['./number-range.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(NumberRangeComponent),
    standalone: false
})
export class NumberRangeComponent extends CustomFormControlAccess implements OnInit, OnDestroy {

    @Input() controlLabel = 'Montant';

    @Input() startPlaceholder = 'A partir de';

    @Input() endPlaceholder = 'Jusqu\'à';

    @Input() id = 'number_range_filter';

    @Input() icon;

    @Input()
    @Coerce('boolean')
    shouldDisplayNumberEnd = false;

    @Input()
    betweenLabel = 'à';

    @Input()
    inputType: 'currency' | 'number' = 'currency';

    form: FormGroup<NumberRangeFormGroupType>;

    readonly errorsMessage: { [key: string]: string } = {
        invalidendNumber: 'La valeur de fin doit être supérieure à la valeur de début.'
    };

    constructor(private readonly fb: FormBuilder) {
        super();
    }

    get isCurrency(): boolean {
        return this.inputType === 'currency';
    }

    get inputIcon(): string {
        return this.isCurrency ? 'euro' : '';
    }

    writeValue(value: NumberRangeType): void {
        this.form.patchValue({
            startNumber: value?.startNumber ?? null,
            endNumber: value?.endNumber ?? null
        }, {emitEvent: false});

        if (this.form.value.endNumber && !this.shouldDisplayNumberEnd) {
            this.toggleNumberEnd();
        }
    }

    validate(c: AbstractControl): Observable<any> {
        return of(this.form.valid ? null : {numberRange: true});
    }

    toggleNumberEnd(): void {
        this.shouldDisplayNumberEnd = !this.shouldDisplayNumberEnd;
        if (!this.shouldDisplayNumberEnd) {
            this.form.get('endNumber').patchValue(null);// emit volontaire
        }
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            startNumber: this.fb.control(null, [FormValidator.number]),
            endNumber: this.fb.control(null, [FormValidator.number])
        }, {
            validator: [
                (form: FormGroup): { invalidendNumber: boolean } | null => {
                    const {startNumber, endNumber} = form.value;
                    const isValid = ['startNumber', 'endNumber'].every(e => form.get(e).valid);
                    const isNumber = [startNumber, endNumber].every(e => e !== '' && e !== null && !isNaN(Number(e)));
                    return isValid && isNumber && startNumber >= endNumber
                        ? {invalidendNumber: true}
                        : null;
                }
            ]
        });

        this.watch$ = this.form.valueChanges.pipe(
            tap(formValues => {
                this.notifyUpdate(formValues);
            })
        );
    }
}
